/**
 * Setup by photo: point the phone at the kitchen or a supplier receipt, and the app SUGGESTS what to add
 * (equipment and surfaces to clean, fridges to log, foods, menu items, supplies with supplier and price, the
 * sanitizer in use). A person reviews the list and accepts what's right; nothing is applied silently
 * (FRAMEWORK T8). Matching and the TCS call are pure functions over data already in core (T7), so a model's
 * guess never decides which guide, supply or temperature rule applies (T3: truth comes from the rules, not the model).
 *
 * The API reads the photo (Gemini raw extraction, then Claude interpretation into SetupSuggestion[]), then calls
 * `enrichSuggestions` here to fill `match` and the TCS field, and later `applySetup` with the ids a person accepted.
 */
import { dryFoodCall } from "./nontcs.ts";
import { SUPPLY_SEEDS, type Supply } from "./actions.ts";
import type { Location, LocationFoodItem } from "./model.ts";
import { MENU_ITEMS, type MenuItem } from "./playbooks.ts";
import { SANITIZER_PRODUCTS, type SanitizerProduct, type SanitizerSetup } from "./sanitizer.ts";
import { getSurface, searchSurfaces, type SurfaceGuide } from "./surfaces.ts";
import { TASK_SEEDS, type TaskSeed } from "./tasks.ts";

export type SetupKind = "equipment" | "surface" | "cold_unit" | "food_item" | "menu_item" | "supply" | "supplier" | "sanitizer"
  /** Room walk photos (roomwalk.ts): an item by type, a spotted issue, a label's verbatim fields. Applied from the walk only. */
  | "walk_item" | "walk_issue" | "walk_label";
/** The kinds a kitchen or receipt photo may suggest (the walk kinds are never in this list). */
export const SETUP_KINDS: readonly SetupKind[] = ["equipment", "surface", "cold_unit", "food_item", "menu_item", "supply", "supplier", "sanitizer"];

export type SetupMatchType = "surface" | "task_seed" | "menu_item" | "supply" | "sanitizer_product";
export interface SetupMatch { type: SetupMatchType; id: string; name: string }

export type SetupFieldValue = string | number | boolean | null;

/**
 * One thing the photo showed, and what the app would do with it.
 * Field conventions by kind (all optional):
 * - cold_unit: `unitKind` "fridge" | "freezer"
 * - food_item: `tcs` true | false | null (null = not sure: ask), `tcsReason`, `dateOnLabel`
 * - supply: `productName`, `quantity`, `unit`, `unitPriceCents`, `supplierName`, `supplierPhone`
 * - supplier: `name`, `phone`, `address`, `receiptDate`
 * - sanitizer: `method` "mix" | "dispenser" | "ready_to_use", `chemical` "quat" | "chlorine" | "iodine", `product`
 */
export interface SetupSuggestion {
  id: string;
  kind: SetupKind;
  /** What the photo showed, in the photo's words ("Milk fridge", "Clorox Disinfecting Wipes 75ct"). */
  label: string;
  /** Filled by core matchers only, never trusted from a model. */
  match?: SetupMatch;
  fields: Record<string, SetupFieldValue>;
  /** 0–1, how sure the reader is that the photo shows this. */
  confidence: number;
  /** Short text: where in the photo, or which receipt line. */
  evidence: string;
}

/* ─────────────── matchers ─────────────── */

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const words = (s: string) => norm(s).split(" ").filter((w) => w.length > 1);
const has = (label: string, re: RegExp) => re.test(norm(label));

/**
 * The surface guide (surfaces.ts) for what the photo showed. Only a match on the guide's name, keywords or materials
 * counts; a word that only appears somewhere in its steps is too weak to call it the same thing. Works with an empty
 * SURFACES list (returns undefined).
 */
export function matchSurface(label: string): SurfaceGuide | undefined {
  const ws = words(label);
  if (!ws.length) return undefined;
  for (const s of searchSurfaces(label)) {
    const hay = norm([s.name, ...s.keywords, ...(s.materials ?? [])].join(" "));
    const hits = ws.filter((w) => hay.split(" ").includes(w)).length;
    if (hits > 0 && hits / ws.length >= 0.5) return s;
    return undefined; // the best candidate isn't close enough; weaker ones won't be either
  }
  return undefined;
}

/** Equipment that already has a starter checklist item (tasks.ts TASK_SEEDS), for when no surface guide exists yet. */
const EQUIPMENT_SEEDS: [RegExp, string][] = [
  [/\bsteam(ing)? wand\b|\bmilk steamer\b/, "coffee-wand"],
  [/\bespresso\b|\bgroup ?head\b|\bportafilter\b/, "coffee-backflush"],
  [/\bgrinder\b/, "coffee-grinder"],
  [/\b(fridge|refrigerator|reach[- ]?in|walk[- ]?in|freezer|cooler)\b/, "fridge-deep"],
  [/\bhood filter|\bexhaust hood\b|\bhood\b/, "hood-filters"],
  [/\bfloor drain\b|\bdrain\b/, "drains"],
  [/\brestroom\b|\btoilet\b|\bbathroom\b/, "restroom-clean"],
];

export function matchTaskSeed(label: string): TaskSeed | undefined {
  const n = norm(label);
  const key = EQUIPMENT_SEEDS.find(([re]) => re.test(n))?.[1];
  return key ? TASK_SEEDS.find((t) => t.key === key) : undefined;
}

/** Words people use for each menu item (ids from playbooks.ts MENU_ITEMS). Checked in order; first hit wins. */
const MENU_ALIASES: [RegExp, string][] = [
  [/\bcold brew\b/, "cold-brew"],
  [/\b(lattes?|cappuccinos?|flat whites?|cortados?|macchiatos?|mochas?|steamed milk|chai lattes?|hot chocolates?)\b/, "milk-drinks"],
  [/\b(espressos?|americanos?|drip coffee|brewed coffee|pour overs?|hot coffee)\b/, "espresso-drinks"],
  [/\b(eclairs?|cream puffs?|custards?|cream filled|cannolis?|cheesecakes?)\b/, "cream-pastries"],
  [/\b(croissants?|danish(es)?|pastry|pastries|bagels?)\b/, "bought-pastries"],
  [/\b(muffins?|scones?|cookies?)\b/, "house-bakes"],
  [/\bbreakfast (sandwich|burrito)\b|\begg sandwich\b/, "breakfast-sandwich"],
  [/\bburgers?\b/, "smash-burger"],
  [/\bfries\b|\bfrench fry\b/, "fries"],
  [/\baioli\b|\bhouse sauce\b|\bmayo(nnaise)?\b/, "house-sauces"],
  [/\b(chili|soup|soups|beans)\b/, "chili-soups"],
  [/\b(steak|prime rib|roast beef)\b/, "steak"],
  [/\bbaked potato/, "baked-potatoes"],
  [/\b(fish|salmon|cod|halibut|tilapia|trout)\b/, "fish"],
  [/\bbuffet\b/, "buffet-line"],
  [/\b(salad|cut fruit|fruit cup|melon)\b/, "cut-salad"],
];

/**
 * Strict: only the alias words (no "most words shared" guess), for places where a wrong dish is harmful, like the
 * allergen line on a food label: "Milk" must never become "Lattes and steamed-milk drinks" (allergens.ts).
 */
export function matchMenuItemByAlias(label: string): MenuItem | undefined {
  const id = MENU_ALIASES.find(([re]) => re.test(norm(label)))?.[1];
  return id ? MENU_ITEMS.find((m) => m.id === id) : undefined;
}

/** The menu item (playbooks.ts) a label names: alias words first, then most words shared with the item's name. */
export function matchMenuItem(label: string): MenuItem | undefined {
  const n = norm(label);
  const id = MENU_ALIASES.find(([re]) => re.test(n))?.[1];
  if (id) return MENU_ITEMS.find((m) => m.id === id);
  const ws = words(label).filter((w) => !["and", "the", "made", "in", "house", "drinks"].includes(w));
  let best: { m: MenuItem; score: number } | undefined;
  for (const m of MENU_ITEMS) {
    const name = words(m.name);
    const score = ws.filter((w) => name.includes(w)).length;
    if (score > 0 && (!best || score > best.score)) best = { m, score };
  }
  return best && best.score / Math.max(ws.length, 1) >= 0.5 ? best.m : undefined;
}

/** Distinctive words that identify each sanitizer product in SANITIZER_PRODUCTS (brand lines are named there). */
const SANITIZER_ALIASES: [RegExp, string][] = [
  [/\bsteramine\b/, "Steramine Sanitizing Tablets"],
  [/\boasis 146\b|\bmulti quat\b/, "Ecolab Oasis 146 Multi-Quat"],
  [/\bkay 5\b/, "Kay-5 Sanitizer"],
  [/\bgermicidal bleach\b/, "Clorox Germicidal Bleach (commercial)"],
  [/\biodophor\b/, "Iodophor sanitizer (e.g., BTF Iodophor)"],
  [/\banywhere\b.*\b(no rinse|sanitiz)/, "CloroxPro Clorox Anywhere No Rinse Sanitizer"],
  [/\bpurell\b.*\b(foodservice|surface)\b/, "PURELL Foodservice Surface Sanitizer"],
  [/\bpurell\b.*\b(foodservice|surface)\b/, "PURELL Foodservice Surface Sanitizer"],
];

/** Supply keys (actions.ts SUPPLY_SEEDS) by product words. Order matters: specific before general. */
const SUPPLY_ALIASES: [RegExp, string][] = [
  [/\bqt ?40\b|\btest strips?\b|\bstrips?\b.*\b(quat|chlorine|iodine|ppm)\b|\bhydrion\b/, "test-strips"],
  [/\bdisinfect/, "disinfectant"],
  [/\bbleach\b/, "bleach"],
  [/\bsanitiz(er|ing)\b|\bquat\b/, "sanitizer"],
  [/\bhand soap\b|\bhand wash\b|\bfoaming soap\b|\bhandwash\b/, "hand-soap"],
  [/\bpaper towels?\b|\bc ?fold\b|\bmulti ?fold\b|\broll towels?\b|\bhand towels?\b/, "paper-towels"],
  [/\btoilet (paper|tissue)\b|\bbath tissue\b/, "toilet-paper"],
  [/\btrash bags?\b|\bcan liners?\b|\bgarbage bags?\b/, "trash-bags"],
  [/\bgloves?\b/, "gloves"],
  [/\bmop\b/, "kitchen-mop"],
  [/\bwiping cloths?\b|\bbar towels?\b|\bmicrofiber\b|\brags?\b/, "wiping-cloths"],
  [/\bthermometer\b/, "thermometer"],
];

export interface SupplyMatch { supply: Supply; product?: SanitizerProduct }

/**
 * What a receipt line or label is, as a supply the location keeps. A known sanitizer product also returns its
 * product entry (method, chemical, matching strip), so the app can suggest the location's sanitizer setup.
 * "Clorox Disinfecting Wipes" → disinfectant; "Hydrion QT-40" → test-strips; "Steramine" → sanitizer (+ quat, mix).
 */
export function matchSupply(productName: string): SupplyMatch | undefined {
  const n = norm(productName);
  const seed = (key: string) => SUPPLY_SEEDS.find((s) => s.key === key);
  const productName2 = SANITIZER_ALIASES.find(([re]) => re.test(n))?.[1];
  const product = productName2 ? SANITIZER_PRODUCTS.find((p) => p.name === productName2) : undefined;
  if (product) {
    const s = seed("sanitizer");
    return s ? { supply: s, product } : undefined;
  }
  const key = SUPPLY_ALIASES.find(([re]) => re.test(n))?.[1];
  const s = key ? seed(key) : undefined;
  return s ? { supply: s } : undefined;
}

/* ─────────────── TCS ─────────────── */

export interface TcsCall { tcs: boolean | null; reason: string; citation: string }

const TCS_GUIDE = "Food Code 1-201.10(B); library: Which foods need temperature control (TCS)";
/** Butter (see foodIsTcs): shown on screen as the reason we don't say TCS or not. */
export const BUTTER_TCS_REASON = "The Food Code doesn't name butter. It depends on its salt, acidity and moisture. Check the package, or ask your health department.";
export const BUTTER_TCS_CITATION = "FDA Food Code 2022 1-201.10(B) \"Time/temperature control for safety food\" (2)(a), (3)(c), Tables A and B (https://www.fda.gov/media/164194/download); IFT report for FDA, \"Evaluation and Definition of Potentially Hazardous Foods\" (Dec 31, 2001), ch. 4 §7.4 and ch. 8 §3.3 (https://www.fda.gov/files/food/published/Evaluation-and-Definition-of-Potentially-Hazardous-Foods.pdf)";
const BAKES = "Food Code 1-201.10(B); playbooks house-bakes (dry bakes are not TCS)";
/** Words that make a dry-sounding name wet (raw dough, a dip, a sauce, a pudding): the "usually not TCS" rules below never apply. */
const WET = /\b(dough|batter|salsa|queso|dip|dips|guacamole|hummus|cheese|sauce|pudding|smoothie|filled|filling|frosting|frosted|mousse|ganache)\b/;

/**
 * Does this food need time/temperature control? Uses only the lists already in core: the library's TCS guide
 * (meat, poultry, fish, shellfish, eggs, milk and dairy; cooked rice, pasta, beans, potatoes and vegetables; tofu,
 * sprouts; cut melon, tomatoes and leafy greens; garlic in oil; usually not: bread, crackers, chips, hard cookies,
 * whole uncut produce, unopened cans) and the menu playbooks (cream or custard fillings TCS; dry bakes not).
 * Anything else is `null`: not sure, so a person decides. It never guesses.
 */
export function foodIsTcs(label: string): TcsCall {
  const n = norm(label);
  const yes = (reason: string, citation = TCS_GUIDE): TcsCall => ({ tcs: true, reason, citation });
  const no = (reason: string, citation = TCS_GUIDE): TcsCall => ({ tcs: false, reason, citation });
  // Cream, custard, whipped cream or cream cheese inside anything makes it TCS (checked before the dry-bake rule).
  if (/\b(custard|cream filled|whipped cream|cream cheese|eclair|cream puff|cheesecake)\b/.test(n)) return yes("Cream or custard filling", "Food Code 1-201.10(B), 3-501.16; playbooks cream-pastries");
  // Plant "milks" are not on the list: ask.
  if (/\b(oat|almond|soy|coconut|rice|cashew|pea|hemp|macadamia) (milk|beverage|drink|creamer)\b/.test(n)) return { tcs: null, reason: "Plant milks aren't on our TCS list. Check the carton, or ask your health department.", citation: TCS_GUIDE };
  if (/\bgarlic\b.*\boil\b/.test(n)) return yes("Garlic-in-oil mixture");
  // Dry foods (nontcs.ts): every word must be one we know, so "Chocolate mousse" or "Chocolate covered strawberries" never lands here.
  const dry = dryFoodCall(label);
  if (dry) return no(dry.reason, dry.citation);
  if (/\bcut\b.*\b(melon|cantaloupe|honeydew|watermelon|tomato|tomatoes|lettuce|greens)\b|\b(sliced|diced|chopped)\b.*\b(melon|cantaloupe|honeydew|watermelon|tomato|tomatoes|lettuce|greens)\b|\b(salad mix|spring mix|bagged salad|pre cut greens)\b/.test(n)) return yes("Cut melon, tomatoes or leafy greens");
  if (/\bcold brew\b/.test(n)) return yes("Cold brew is held cold", "playbooks cold-brew; Food Code 3-501.16");
  if (/\bcooked\b.*\b(rice|pasta|noodles|beans|potato|potatoes|vegetables|veggies)\b|\bbaked potato/.test(n)) return yes("Cooked rice, pasta, beans, potatoes or vegetables");
  if (/\b(tofu|tempeh|soy protein|sprouts)\b/.test(n)) return yes("Tofu, soy protein or sprouts");
  if (/\b(beef|pork|chicken|turkey|poultry|lamb|sausage|ham|meat|steak|burger|patty|patties|bacon)\b/.test(n)) return yes("Meat or poultry");
  if (/\b(fish|salmon|tuna|cod|halibut|tilapia|trout|shrimp|crab|lobster|oyster|oysters|clam|clams|mussel|mussels|scallop|scallops|shellfish)\b/.test(n)) return yes("Fish or shellfish");
  if (/\b(egg|eggs)\b/.test(n)) return yes("Eggs");
  // Butter: no official FDA source names it TCS or non-TCS (checked 2026-09-29), so we don't either. The 2022 Food Code
  // (with its Annexes and the Dec 2024 Supplement Utah adopts) never names butter in the TCS definition. 1-201.10(B)
  // "Time/temperature control for safety food" (2)(a) includes "An animal FOOD that is raw or heat-treated", and (3)(c)
  // excludes "A FOOD that because of its PH or AW value, or interaction of AW and PH values, is designated as a non-TCS
  // FOOD in Table A or B". The IFT report written for FDA ("Evaluation and Definition of Potentially Hazardous Foods",
  // 2001, ch. 4 §7.4, p. 37): "The need for time/temperature control depends on the pH and aw of the product, and on whether
  // other preservatives have been added"; its ch. 8 §3.3 worked examples put some salted butters at non-TCS and others at
  // "may be a TCS food". So it depends on the product: null, with that reason. Nut and fruit "butters" aren't butter;
  // garlic or herb butter is a mixture and gets the plain "not on our list" answer.
  if (/\bbutter( (block|blocks|stick|sticks|pat|pats|cup|cups|chip|chips))?$/.test(n) && !/\b(peanut|almond|cashew|sunflower|apple|nut|cocoa|shea|seed|pumpkin|soy|cookie|garlic|herb|herbed|compound|honey|cinnamon)\s+butter\b/.test(n)) {
    return { tcs: null, reason: BUTTER_TCS_REASON, citation: BUTTER_TCS_CITATION };
  }
  if (/\b(milk|buttermilk|cream|half and half|yogurt|dairy)\b/.test(n)) return yes("Milk and dairy");
  // A wet part makes the name more than the dry food it starts with ("Cookie dough", "Chips and salsa", "Banana pudding"): not ours to say.
  if (WET.test(n)) return { tcs: null, reason: "Not on our TCS list. Check the label, or ask your health department.", citation: TCS_GUIDE };
  if (/\b(croissant|croissants|muffin|muffins|scone|scones|cookie|cookies|biscotti)\b/.test(n)) return no("Dry baked goods without cream", BAKES);
  if (/\b(bread|bagel|bagels|crackers|chips|pretzels)\b/.test(n)) return no("Bread, crackers and chips are usually not TCS");
  if (/\bwhole\b.*\b(fruit|apple|apples|banana|bananas|orange|oranges|melon|tomato|tomatoes)\b|\b(apple|apples|banana|bananas|orange|oranges)\b/.test(n)) return no("Whole uncut produce");
  if (/\b(unopened|canned|can of)\b/.test(n)) return no("Unopened canned food");
  return { tcs: null, reason: "Not on our TCS list. Check the label, or ask your health department.", citation: TCS_GUIDE };
}

/* ─────────────── enrich (core decides matches, never the model) ─────────────── */

const clamp01 = (x: number) => (Number.isFinite(x) ? Math.min(1, Math.max(0, x)) : 0);

/**
 * Fills `match` (and the TCS call for foods) from core data, dropping any `match` a model returned. A supply that is a
 * known sanitizer product also yields a "sanitizer" suggestion for the location's setup (id `<id>-sanitizer`), unless
 * the reader already suggested one for the same product. Pure; stable order.
 */
export function enrichSuggestions(input: SetupSuggestion[]): SetupSuggestion[] {
  const out: SetupSuggestion[] = [];
  const sanitizerProducts = new Set<string>();
  const base = input.map((s) => ({ ...s, fields: { ...s.fields }, confidence: clamp01(s.confidence), match: undefined as SetupMatch | undefined }));
  for (const s of base) {
    switch (s.kind) {
      case "equipment":
      case "surface":
      case "cold_unit": {
        const g = matchSurface(s.label);
        if (g) s.match = { type: "surface", id: g.id, name: g.name };
        else if (s.kind !== "cold_unit") {
          const t = matchTaskSeed(s.label);
          if (t) s.match = { type: "task_seed", id: t.key, name: t.title };
        }
        if (s.kind === "cold_unit" && s.fields.unitKind !== "freezer") s.fields.unitKind = has(s.label, /\bfreezer\b/) ? "freezer" : "fridge";
        break;
      }
      case "menu_item": {
        const m = matchMenuItem(s.label);
        if (m) s.match = { type: "menu_item", id: m.id, name: m.name };
        break;
      }
      case "food_item": {
        const call = foodIsTcs(s.label);
        s.fields.tcs = call.tcs;
        s.fields.tcsReason = call.reason;
        break;
      }
      case "supply": {
        const m = matchSupply(String(s.fields.productName ?? s.label));
        if (m) s.match = { type: "supply", id: m.supply.key, name: m.supply.name };
        break;
      }
      case "sanitizer": {
        const m = matchSupply(String(s.fields.product ?? s.label));
        if (m?.product) {
          s.match = { type: "sanitizer_product", id: m.product.name, name: m.product.name };
          s.fields.method = m.product.method;
          s.fields.chemical = m.product.chemical ?? null;
          s.fields.product = m.product.name;
          sanitizerProducts.add(m.product.name);
        }
        break;
      }
      case "supplier":
        break;
    }
  }
  for (const s of base) {
    out.push(s);
    if (s.kind !== "supply") continue;
    const m = matchSupply(String(s.fields.productName ?? s.label));
    if (!m?.product || sanitizerProducts.has(m.product.name)) continue;
    sanitizerProducts.add(m.product.name);
    out.push({
      id: `${s.id}-sanitizer`, kind: "sanitizer", label: s.label,
      match: { type: "sanitizer_product", id: m.product.name, name: m.product.name },
      fields: { method: m.product.method, chemical: m.product.chemical ?? null, product: m.product.name, strip: m.product.strip ?? null },
      confidence: s.confidence, evidence: `${s.evidence} (${m.product.name} is ${m.product.method === "ready_to_use" ? "a ready-to-use spray" : `${m.product.chemical ?? "a"} sanitizer you ${m.product.method === "mix" ? "mix" : "dispense"}`})`,
    });
  }
  return out;
}

/* ─────────────── apply (only what a person accepted) ─────────────── */

/** A checklist item to create for a newly added surface or piece of equipment. */
export interface SetupTaskSeed {
  key: string; // "surface:<id>" or "seed:<task key>"
  title: string;
  how: string;
  category: TaskSeed["category"];
  schedule: TaskSeed["schedule"];
  requirementIds: string[];
  surfaceId?: string;
}

export interface SetupApplyResult {
  /** Only the Location fields that change. Empty when nothing new was accepted (idempotent). */
  patch: Partial<Pick<Location, "menuItemIds" | "areas" | "supplies" | "sanitizer" | "foodItems">>;
  taskSeeds: SetupTaskSeed[];
  /** Suggestion ids that changed something. */
  applied: string[];
  /** Accepted suggestions that couldn't be applied, with why (so nothing is silently dropped). */
  skipped: { id: string; reason: string }[];
}

/** Schedule from a surface guide's "how often" text; every shift when it doesn't say (seeded tasks stay editable). */
function scheduleFor(g: SurfaceGuide): TaskSeed["schedule"] {
  const text = g.howOften.map((h) => h.when.toLowerCase()).join(" ");
  if (/\b(each use|every use|4 hours|shift|daily|every day|end of (the )?day|close|closing|opening)\b/.test(text)) return { perShift: true };
  if (/\bweek(ly)?\b/.test(text)) return { everyDays: 7 };
  if (/\bmonth(ly)?\b/.test(text)) return { everyDays: 30 };
  return { perShift: true };
}

const lc = (s: string) => s.trim().toLowerCase();
const str = (v: SetupFieldValue | undefined) => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const intOrUndef = (v: SetupFieldValue | undefined, max: number) => (typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= max ? Math.round(v) : undefined);

/**
 * Turns the suggestions a person accepted into a Location patch and checklist seeds. Pure and idempotent: applying
 * the same suggestions to the patched location (with the created task titles) returns an empty patch and no seeds.
 * - menu_item (matched) → menuItemIds; obligations are recomputed from the menu (obligationsFor)
 * - cold_unit → areas (the fridge log's unit names)
 * - food_item → foodItems (quick picks for the food temperature log)
 * - supply (matched) → supplies merged by key: the receipt's supplier, unit and price fill in; where it's kept, par
 *   and counts are kept; a supplier suggestion on the same receipt fills in lines that don't name one
 * - sanitizer (matched product) → sanitizer setup (method added, chemical and product set)
 * - equipment / surface (matched) → one checklist seed each, skipped when a task with that title already exists
 */
export function applySetup(location: Location, accepted: SetupSuggestion[], opts: { existingTaskTitles?: string[] } = {}): SetupApplyResult {
  const applied = new Set<string>();
  const skipped: { id: string; reason: string }[] = [];
  const menu = [...location.menuItemIds];
  const areas = [...location.areas];
  const foods: LocationFoodItem[] = (location.foodItems ?? []).map((f) => ({ ...f }));
  const supplies: Supply[] = (location.supplies ?? []).map((s) => ({ ...s, supplier: s.supplier ? { ...s.supplier } : undefined }));
  let sanitizer: SanitizerSetup | undefined = location.sanitizer ? { ...location.sanitizer, methods: [...location.sanitizer.methods] } : undefined;
  const titles = new Set((opts.existingTaskTitles ?? []).map(lc));
  const taskSeeds: SetupTaskSeed[] = [];
  const seedKeys = new Set<string>();

  const supplierSug = accepted.find((s) => s.kind === "supplier" && str(s.fields.name ?? s.label));
  const defaultSupplier = supplierSug ? { name: str(supplierSug.fields.name) ?? supplierSug.label.trim(), phone: str(supplierSug.fields.phone) } : undefined;

  for (const s of accepted) {
    switch (s.kind) {
      case "menu_item": {
        if (s.match?.type !== "menu_item" || !MENU_ITEMS.some((m) => m.id === s.match!.id)) { skipped.push({ id: s.id, reason: "No matching menu item in our playbooks." }); break; }
        if (!menu.includes(s.match.id)) { menu.push(s.match.id); applied.add(s.id); }
        break;
      }
      case "cold_unit": {
        const name = s.label.trim();
        if (!name) { skipped.push({ id: s.id, reason: "No name for this unit." }); break; }
        if (!areas.some((a) => lc(a) === lc(name))) { areas.push(name); applied.add(s.id); }
        break;
      }
      case "food_item": {
        const name = s.label.trim();
        if (!name) { skipped.push({ id: s.id, reason: "No name for this food." }); break; }
        const tcs = foodIsTcs(name).tcs; // core decides, not the stored field
        const cur = foods.find((f) => lc(f.name) === lc(name));
        if (!cur) { foods.push({ name, tcs }); applied.add(s.id); }
        break;
      }
      case "supply": {
        if (s.match?.type !== "supply") { skipped.push({ id: s.id, reason: "Not one of the supplies we track yet. Add it in Settings." }); break; }
        const seed = SUPPLY_SEEDS.find((x) => x.key === s.match!.id);
        if (!seed) { skipped.push({ id: s.id, reason: "Unknown supply." }); break; }
        const supplierName = str(s.fields.supplierName) ?? defaultSupplier?.name;
        const supplierPhone = str(s.fields.supplierPhone) ?? (supplierName === defaultSupplier?.name ? defaultSupplier?.phone : undefined);
        const unit = str(s.fields.unit);
        const price = intOrUndef(s.fields.unitPriceCents, 10_000_000);
        const idx = supplies.findIndex((x) => x.key === seed.key);
        const before = idx >= 0 ? JSON.stringify(supplies[idx]) : null;
        const cur: Supply = idx >= 0 ? supplies[idx]! : { ...seed };
        if (supplierName) cur.supplier = { ...(cur.supplier?.name === supplierName ? cur.supplier : {}), name: supplierName, ...(supplierPhone ? { phone: supplierPhone } : {}) };
        if (unit) cur.unit = unit;
        if (price !== undefined) cur.unitPriceCents = price;
        if (cur.supplier && !cur.supplier.phone) delete cur.supplier.phone;
        if (idx < 0) { supplies.push(cur); applied.add(s.id); }
        else if (JSON.stringify(cur) !== before) applied.add(s.id);
        break;
      }
      case "sanitizer": {
        const product = s.match?.type === "sanitizer_product" ? SANITIZER_PRODUCTS.find((p) => p.name === s.match!.id) : undefined;
        if (!product) { skipped.push({ id: s.id, reason: "Not a sanitizer we know. Set it in Settings." }); break; }
        const next: SanitizerSetup = { methods: sanitizer?.methods.includes(product.method) ? [...sanitizer.methods] : [...(sanitizer?.methods ?? []), product.method], ...(product.chemical ? { chemical: product.chemical } : sanitizer?.chemical ? { chemical: sanitizer.chemical } : {}), product: product.name };
        if (JSON.stringify(next) !== JSON.stringify(sanitizer)) { sanitizer = next; applied.add(s.id); }
        break;
      }
      case "equipment":
      case "surface": {
        let seed: SetupTaskSeed | undefined;
        if (s.match?.type === "surface") {
          const g = getSurface(s.match.id);
          if (g) seed = { key: `surface:${g.id}`, title: `Clean the ${g.name.toLowerCase()}`, how: g.steps.join(" "), category: "cleaning", schedule: scheduleFor(g), requirementIds: g.requirementIds.length ? [...g.requirementIds] : ["fc-cleaning-frequency"], surfaceId: g.id };
        } else if (s.match?.type === "task_seed") {
          const t = TASK_SEEDS.find((x) => x.key === s.match!.id);
          if (t) seed = { key: `seed:${t.key}`, title: t.title, how: t.how, category: t.category, schedule: { ...t.schedule }, requirementIds: [...t.requirementIds] };
        }
        if (!seed) { skipped.push({ id: s.id, reason: "No cleaning guide for this yet. Add a checklist item by hand if it needs one." }); break; }
        if (titles.has(lc(seed.title)) || seedKeys.has(seed.key)) break;
        seedKeys.add(seed.key);
        titles.add(lc(seed.title));
        taskSeeds.push(seed);
        applied.add(s.id);
        break;
      }
      case "supplier": {
        // Used above as the default supplier for the receipt's lines. Applied when it filled at least one line.
        break;
      }
      case "walk_item":
      case "walk_issue":
      case "walk_label":
        skipped.push({ id: s.id, reason: "Add this from the room walk." });
        break;
    }
  }

  const patch: SetupApplyResult["patch"] = {};
  if (menu.length !== location.menuItemIds.length) patch.menuItemIds = menu;
  if (areas.length !== location.areas.length) patch.areas = areas;
  if (foods.length !== (location.foodItems ?? []).length) patch.foodItems = foods;
  if (JSON.stringify(supplies) !== JSON.stringify(location.supplies ?? [])) patch.supplies = supplies;
  if (JSON.stringify(sanitizer) !== JSON.stringify(location.sanitizer)) patch.sanitizer = sanitizer;
  if (supplierSug && defaultSupplier && patch.supplies?.some((x) => x.supplier?.name === defaultSupplier.name)) applied.add(supplierSug.id);
  return { patch, taskSeeds, applied: accepted.filter((s) => applied.has(s.id)).map((s) => s.id), skipped };
}

/* ─────────────── quick picks for logs ─────────────── */

export interface FoodPick { name: string; tcs: boolean | null; source: "setup" | "menu" }

/** Menu items whose steps carry a food temperature check (temp_cook_hold or cooling). */
const LOGGED_MENU = (ids: string[]) => MENU_ITEMS.filter((m) => ids.includes(m.id) && m.steps.some((s) => s.check && (s.check.templateId === "temp_cook_hold" || s.check.templateId === "cooling")));

/**
 * What the log screens can offer as one-tap answers: `food` for the food temperature and cooling logs (the location's
 * foods from setup, TCS first, then menu items that are temperature-checked) and `unit` for the fridge log (areas that
 * are cold units). Pure; the same on every screen.
 */
export function quickPicks(location: Pick<Location, "menuItemIds" | "areas" | "foodItems">): { food: FoodPick[]; unit: string[] } {
  const seen = new Set<string>();
  const food: FoodPick[] = [];
  const rank = (t: boolean | null) => (t === true ? 0 : t === null ? 1 : 2);
  for (const f of [...(location.foodItems ?? [])].sort((a, b) => rank(a.tcs) - rank(b.tcs))) {
    if (seen.has(lc(f.name))) continue;
    seen.add(lc(f.name));
    food.push({ name: f.name, tcs: f.tcs, source: "setup" });
  }
  for (const m of LOGGED_MENU(location.menuItemIds)) {
    if (seen.has(lc(m.name))) continue;
    seen.add(lc(m.name));
    food.push({ name: m.name, tcs: m.tcs, source: "menu" });
  }
  const unit = location.areas.filter((a) => /\b(fridge|refrigerator|freezer|cooler|walk[- ]?in|reach[- ]?in|cold well)\b/i.test(a));
  return { food, unit };
}

/* ─────────────── the stored scan ─────────────── */

/** "room" and "label": photos on the room walk (roomwalk.ts); a room photo names types, a label photo reads brand, model and serial. */
export type SetupScanKind = "kitchen" | "receipt" | "room" | "label";
export const SETUP_SCAN_KINDS: readonly SetupScanKind[] = ["kitchen", "receipt", "room", "label"];
export type SetupScanStatus = "processing" | "ready" | "applied" | "duplicate" | "failed";

/**
 * One setup photo and what it suggested. Stored privately (image and row); `suggestions` are proposals until a person
 * applies some by id. Receipts double as the approved-source record (Food Code 3-201.11).
 */
export interface SetupScan {
  id: string;
  orgId: string;
  locationId: string;
  kind: SetupScanKind;
  imagePath: string;
  imageSha256: string;
  capturedAt: string;
  capturedBy: string;
  status: SetupScanStatus;
  /** "ai": Gemini read + Claude interpretation. "demo": fixed sample suggestions (no keys; demo only). */
  engine: "ai" | "demo";
  /** Verbatim first-pass reading, kept for audit. */
  rawExtraction?: unknown;
  suggestions: SetupSuggestion[];
  /** Receipt header, for the binder's supplier-receipts list. */
  receipt?: { supplierName?: string; supplierPhone?: string; supplierAddress?: string; date?: string };
  notes?: string;
  duplicateOf?: string;
  appliedIds?: string[];
  appliedAt?: string;
  appliedBy?: string;
  error?: string;
  /** Room walk photos: the walk and the room they belong to. */
  walkId?: string;
  roomKey?: string;
  /**
   * How far to turn the stored picture, clockwise, so it stands upright (photo orientation, 2026-10-15). Stored pictures carry no
   * metadata (services/api uploads.ts), so the camera's own Orientation tag is read at upload and kept here; "Rotate" adds 90.
   * The API writes it back into the picture it serves as a one-tag EXIF Orientation (no location, nothing else). 0 when absent.
   */
  rotation?: PhotoRotation;
  updatedAt: string;
}

/** Clockwise quarter turns for a stored photo (SetupScan.rotation). */
export type PhotoRotation = 0 | 90 | 180 | 270;
export const isPhotoRotation = (v: unknown): v is PhotoRotation => v === 0 || v === 90 || v === 180 || v === 270;
/** "Rotate": a quarter turn clockwise from where it is now. */
export const nextRotation = (r: number | null | undefined): PhotoRotation => (((isPhotoRotation(r) ? r : 0) + 90) % 360) as PhotoRotation;
